import { useState } from 'react';
import {
  useBranches,
  useCreateBranch,
  useUpdateBranch,
  useDeleteBranch,
} from '../../../hooks/useBranches';
import { useCompanies } from '../../../hooks/useCompanies';
import { ITEMS_PER_PAGE, INITIAL_BRANCH_STATE } from '../constants/branchesConstants';
import { filterBranches } from '../utils/branchesUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

export const useBranchesTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(INITIAL_BRANCH_STATE);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [branchToDelete, setBranchToDelete] = useState(null);

  const { data: branches = [], isLoading } = useBranches();
  const { data: companies = [] } = useCompanies();
  const createMutation = useCreateBranch();
  const updateMutation = useUpdateBranch();
  const deleteMutation = useDeleteBranch();

  const {
    data: paginatedData,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: branches,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterBranches,
  });

  const handleOpenDialog = () => {
    setSelectedBranch(INITIAL_BRANCH_STATE);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSelectedBranch((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = () => {
    if (editMode) {
      updateMutation.mutate(
        { id: selectedBranch.uuid, branch: selectedBranch },
        {
          onSuccess: () => {
            handleCloseDialog();
          },
        }
      );
    } else {
      createMutation.mutate(selectedBranch, {
        onSuccess: () => {
          handleCloseDialog();
        },
      });
    }
  };

  const handleEdit = (branch) => {
    setSelectedBranch(branch);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = (branch) => {
    setBranchToDelete(branch);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (branchToDelete) {
      try {
        await deleteMutation.mutateAsync(branchToDelete.uuid);
        setDeleteConfirmOpen(false);
        setBranchToDelete(null);
      } catch (error) {
        console.error('Error deleting branch:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setBranchToDelete(null);
  };

  return {
    // Data
    data: paginatedData,
    isLoading:
      isLoading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,

    // Pagination
    page,
    totalPages,
    totalCount,
    rowsPerPage,

    // Search
    searchTerm,

    // Dialog state
    dialogOpen,
    editMode,
    selectedBranch,

    // Delete confirmation state
    deleteConfirmOpen,
    branchToDelete,

    // External data
    companies,

    // Actions
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
